import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [stats, setStats] = useState(null);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(isAdmin ? (user?.firmId || 'ALL') : (user?.firmId || '1'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const configRes = await axiosClient.get('/auth/config');
        if (configRes.success) {
          setFirms(configRes.data?.firms || []);
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (isAdmin) fetchFirms();
  }, [isAdmin]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const firmParam = isAdmin ? selectedFirm : (user?.firmId || '');
      const res = await axiosClient.get(`/reports/dashboard?firmId=${firmParam}`);
      if (res.success) {
        setStats(res.data);
      }
    } catch (e) {
      console.error('Error fetching dashboard stats:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [selectedFirm]);

  if (loading && !stats) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      {/* Welcome Banner */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="fw-bold mb-1 text-dark">
            Welcome, <span className="text-primary">{user?.username}</span>
            {isAdmin && <span className="badge bg-danger ms-2 fs-6">Admin</span>}
          </h2>
          <p className="text-muted mb-0 small">
            Branch: <strong className="text-dark">{user?.firmName}</strong> | Financial Year: <span className="badge bg-secondary">FY {user?.fYear}</span>
          </p>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {isAdmin && (
            <div className="d-flex align-items-center gap-1 bg-white border p-1 rounded shadow-sm">
              <i className="bi bi-building text-warning ms-1"></i>
              <select
                className="form-select form-select-sm border-0 bg-transparent fw-semibold"
                style={{ width: '220px' }}
                value={selectedFirm}
                onChange={(e) => setSelectedFirm(e.target.value)}
                title="Filter Dashboard by Firm Branch"
              >
                <option value="ALL">★ All Firms (Consolidated)</option>
                {firms.map((f) => (
                  <option key={f.Id} value={f.Id}>
                    {f.firmName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <Link to="/sales/new" className="btn btn-ocean d-flex align-items-center gap-1">
            <i className="bi bi-cart-plus"></i> New Sale
          </Link>
          <Link to="/payments" className="btn btn-teal d-flex align-items-center gap-1">
            <i className="bi bi-cash-stack"></i> Record Payment
          </Link>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-primary">
            <div className="stat-icon bg-primary bg-opacity-10 text-primary">
              <i className="bi bi-currency-rupee"></i>
            </div>
            <div>
              <div className="text-muted small fw-semibold">TODAY'S SALES</div>
              <h4 className="fw-bold mb-0">₹{parseFloat(stats?.todaySales?.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
              <div className="small text-muted">{stats?.todaySales?.count || 0} bills generated today</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-success">
            <div className="stat-icon bg-success bg-opacity-10 text-success">
              <i className="bi bi-graph-up-arrow"></i>
            </div>
            <div>
              <div className="text-muted small fw-semibold">TOTAL SALES (FY)</div>
              <h4 className="fw-bold mb-0">₹{parseFloat(stats?.totalSales?.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
              <div className="small text-muted">{stats?.totalSales?.count || 0} total sales invoices</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-info">
            <div className="stat-icon bg-info bg-opacity-10 text-info">
              <i className="bi bi-cart-check"></i>
            </div>
            <div>
              <div className="text-muted small fw-semibold">TOTAL PURCHASES (FY)</div>
              <h4 className="fw-bold mb-0">₹{parseFloat(stats?.totalPurchases?.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
              <div className="small text-muted">{stats?.totalPurchases?.count || 0} supplier purchase bills</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-warning">
            <div className="stat-icon bg-warning bg-opacity-10 text-warning">
              <i className="bi bi-wallet-fill"></i>
            </div>
            <div>
              <div className="text-muted small fw-semibold">RECEIPTS COLLECTED</div>
              <h4 className="fw-bold mb-0">₹{parseFloat(stats?.totalReceipts || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
              <div className="small text-muted">Customer payments received</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Action Cards & Recent Sales */}
      <div className="row g-4">
        {/* Recent Invoices Table */}
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0 text-dark">
                <i className="bi bi-clock-history text-primary me-2"></i> Recent Sales Invoices
              </h5>
              <Link to="/sales" className="btn btn-sm btn-outline-primary">
                View All <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Sale No</th>
                      <th>Date</th>
                      {stats?.isAllFirms && <th>Firm</th>}
                      <th>Customer Name</th>
                      <th className="text-end">Grand Total</th>
                      <th className="text-end">Paid Amount</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.recentSales || []).length === 0 ? (
                      <tr>
                        <td colSpan={stats?.isAllFirms ? 7 : 6} className="text-center py-4 text-muted">
                          No sales recorded yet for this financial year.
                        </td>
                      </tr>
                    ) : (
                      (stats?.recentSales || []).map((s) => (
                        <tr key={s.Id}>
                          <td><strong>#{s.SaleNo}</strong></td>
                          <td>{formatDate(s.SaleDate)}</td>
                          {stats?.isAllFirms && (
                            <td>
                              <span className="badge bg-light text-secondary border">{s.firmName || '-'}</span>
                            </td>
                          )}
                          <td className="fw-semibold text-primary">{s.CustomerName || 'Cash Customer'}</td>
                          <td className="text-end fw-bold">₹{parseFloat(s.GrandTotal).toFixed(2)}</td>
                          <td className="text-end text-success">₹{parseFloat(s.PaidAmt).toFixed(2)}</td>
                          <td className="text-center">
                            <Link to={`/sales`} className="btn btn-sm btn-light border py-0 px-2">
                              <i className="bi bi-eye"></i>
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Top Outstanding Parties */}
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0 text-dark">
                <i className="bi bi-exclamation-circle text-danger me-2"></i> Outstanding Balances
              </h5>
              <Link to="/reports/statement" className="btn btn-sm btn-outline-secondary">
                Statements
              </Link>
            </div>
            <div className="card-body p-0">
              <ul className="list-group list-group-flush">
                {(stats?.topDebtors || []).length === 0 ? (
                  <li className="list-group-item text-center text-muted py-4">
                    All accounts are settled.
                  </li>
                ) : (
                  (stats?.topDebtors || []).map((d) => (
                    <li key={d.Id} className="list-group-item d-flex justify-content-between align-items-center py-3">
                      <div>
                        <div className="fw-bold text-dark">{d.AccountName}</div>
                        <small className="text-muted">{d.MobileNo}</small>
                      </div>
                      <span className={`badge ${d.netBalance > 0 ? 'bg-danger' : 'bg-success'} fs-6 px-2 py-1`}>
                        {d.netBalance > 0 ? `Receivable: ₹${parseFloat(d.netBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : `Payable: ₹${Math.abs(d.netBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
